/**
 * OperativeCard — the visual credential.
 *
 * Renders a fixed-aspect card (650 × 1000) that doubles as the PNG export
 * source. Captured by /scripts/cardExport.js. Two color modes:
 *   • Standard (charcoal + classified blue)
 *   • Founding (charcoal + gold)
 *   • Director (charcoal + red)
 *   • LEVEL IV (charcoal + silver)
 */
import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck, Star, FileBadge } from "lucide-react";

const PALETTES = {
  founding: { accent: "#E0A526", glow: "rgba(224,165,38,0.20)", soft: "#3a2b0a", label: "FOUNDING" },
  director: { accent: "#D92D20", glow: "rgba(217,45,32,0.18)", soft: "#3a0e0a", label: "DIRECTOR" },
  silver:   { accent: "#C9CDD4", glow: "rgba(201,205,212,0.12)", soft: "#1a1c20", label: "LEVEL IV" },
  std:      { accent: "#9FA8D4", glow: "rgba(159,168,212,0.10)", soft: "#16171c", label: "ARCHIVE" },
};

const _palette = (card) => {
  if (card.clearance?.is_director) return PALETTES.director;
  if (card.is_founding) return PALETTES.founding;
  if (card.clearance?.rank >= 4) return PALETTES.silver;
  return PALETTES.std;
};

// Build a deterministic 30-bit "barcode" from the serial number.
const _barcode = (serial) => {
  const out = [];
  let h = 0;
  for (let i = 0; i < (serial || "").length; i++) h = (h * 31 + serial.charCodeAt(i)) & 0xffffffff;
  for (let i = 0; i < 60; i++) {
    h = (h * 1103515245 + 12345) & 0x7fffffff;
    out.push(h & 1);
  }
  return out;
};

const MicroText = ({ children }) => (
  <div
    aria-hidden
    style={{
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 4.5,
      letterSpacing: "0.18em",
      color: "rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "clip",
      lineHeight: 1.1,
    }}
  >
    {children}
  </div>
);

const ArchiveSeal = ({ accent }) => (
  <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden>
    <defs>
      <radialGradient id="sealGrad" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
        <stop offset="80%" stopColor={accent} stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="22" cy="22" r="20" fill="url(#sealGrad)" />
    <circle cx="22" cy="22" r="20" fill="none" stroke={accent} strokeWidth="0.7" strokeOpacity="0.7" />
    <circle cx="22" cy="22" r="14" fill="none" stroke={accent} strokeWidth="0.4" strokeOpacity="0.5" />
    <text x="22" y="26" textAnchor="middle"
          style={{ fontFamily: "serif", fontSize: 16, fill: accent, letterSpacing: 1 }}>
      A
    </text>
  </svg>
);

/**
 * Renders the card. Pass `forwardRef` to capture for export.
 */
const OperativeCard = React.forwardRef(({ card, qrUrl, mode = "screen", hideTestId = false }, ref) => {
  if (!card) return null;
  const pal = _palette(card);
  const bars = _barcode(card.archive_serial || "");
  const joinedShort = (card.joined_at || "").slice(0, 10);
  const issuedShort = (card.issued_at || "").slice(0, 10);

  // Visible QR target: full URL if owner enabled the public profile,
  // otherwise an opaque "PRIVATE" placeholder so the card still looks finished.
  const qrValue = qrUrl
    || (typeof window !== "undefined"
        ? `${window.location.origin}/member/${card.callsign || ""}`
        : `/member/${card.callsign || ""}`);

  return (
    <div
      ref={ref}
      data-testid={hideTestId ? undefined : "operative-card"}
      style={{
        width: 650,
        height: 1000,
        background: "linear-gradient(180deg, #0B0A0E 0%, #07060A 100%)",
        color: "#E4DFD3",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        position: "relative",
        overflow: "hidden",
        boxShadow: `inset 0 0 0 1px ${pal.accent}33, 0 0 60px ${pal.glow}`,
        border: `1px solid ${pal.accent}55`,
      }}
    >
      {/* Paper grain + scanlines */}
      <div
        aria-hidden
        style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: `
            repeating-linear-gradient(0deg, rgba(255,255,255,0.018) 0 1px, transparent 1px 3px),
            radial-gradient(circle at 20% 10%, ${pal.glow}, transparent 60%),
            radial-gradient(circle at 90% 95%, rgba(255,255,255,0.04), transparent 50%)
          `,
        }}
      />
      {/* Crosshair corners */}
      {[
        { t: 16, l: 16, b: null, r: null, brdr: "left top" },
        { t: 16, l: null, b: null, r: 16, brdr: "right top" },
        { t: null, l: 16, b: 16, r: null, brdr: "left bottom" },
        { t: null, l: null, b: 16, r: 16, brdr: "right bottom" },
      ].map((c, i) => (
        <span key={i} aria-hidden style={{
          position: "absolute",
          top: c.t, left: c.l, right: c.r, bottom: c.b,
          width: 14, height: 14,
          borderTop: c.brdr.includes("top") ? `1px solid ${pal.accent}` : "none",
          borderBottom: c.brdr.includes("bottom") ? `1px solid ${pal.accent}` : "none",
          borderLeft: c.brdr.includes("left") ? `1px solid ${pal.accent}` : "none",
          borderRight: c.brdr.includes("right") ? `1px solid ${pal.accent}` : "none",
        }} />
      ))}

      {/* Header */}
      <div style={{ position: "relative", padding: "28px 32px 0", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ArchiveSeal accent={pal.accent} />
            <div>
              <div style={{ fontSize: 11, letterSpacing: "0.45em", color: pal.accent }}>
                THE ARCHIVE
              </div>
              <div style={{ fontSize: 7.5, letterSpacing: "0.4em", color: "#6F6A60", marginTop: 2 }}>
                HIDDEN TRUTHS · EST. 2026
              </div>
            </div>
          </div>
          <div style={{
            border: `1px solid ${pal.accent}88`,
            padding: "4px 10px",
            fontSize: 8,
            letterSpacing: "0.35em",
            color: pal.accent,
            background: "rgba(0,0,0,0.4)",
          }}>
            // {pal.label}
          </div>
        </div>

        <div style={{ marginTop: 28 }}>
          <div style={{ fontSize: 9, letterSpacing: "0.4em", color: "#6F6A60" }}>
            OPERATIVE IDENTIFICATION
          </div>
          <div style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: 64,
            lineHeight: 1.0,
            marginTop: 6,
            color: pal.accent,
            letterSpacing: "0.04em",
            textShadow: `0 0 28px ${pal.glow}`,
          }} data-testid="card-callsign">
            {card.callsign || "OPERATIVE-UNKNOWN"}
          </div>
          <div style={{
            marginTop: 8, fontSize: 11, letterSpacing: "0.35em",
            color: "#A39E93",
          }}>
            ARCHIVE SERIAL · <span style={{ color: "#E4DFD3" }} data-testid="card-serial">{card.archive_serial}</span>
          </div>
        </div>
      </div>

      {/* Clearance + Favorite */}
      <div style={{
        margin: "26px 32px 0", padding: 16,
        border: `1px solid ${pal.accent}33`,
        background: pal.soft,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
      }}>
        <div>
          <div style={{ fontSize: 8, letterSpacing: "0.4em", color: "#6F6A60" }}>
            ARCHIVE CLEARANCE
          </div>
          <div style={{
            fontFamily: "Georgia, serif", fontSize: 28, color: pal.accent,
            letterSpacing: "0.06em", marginTop: 2,
          }} data-testid="card-clearance">
            {card.clearance?.label || "—"}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
            {[1,2,3,4,5].map(i => (
              <Star key={i} size={10}
                fill={i <= (card.clearance?.rank || 0) ? pal.accent : "transparent"}
                color={pal.accent} strokeWidth={1.2} />
            ))}
          </div>
        </div>
        <ShieldCheck size={42} color={pal.accent} strokeWidth={1.3} style={{ opacity: 0.85 }} />
        {card.favorite_category && (
          <div style={{ textAlign: "right", maxWidth: 180 }}>
            <div style={{ fontSize: 8, letterSpacing: "0.4em", color: "#6F6A60" }}>
              FAVORED DOMAIN
            </div>
            <div style={{
              fontFamily: "Georgia, serif", fontSize: 16, color: "#E4DFD3",
              letterSpacing: "0.04em", marginTop: 4,
            }} data-testid="card-favorite-category">
              {card.favorite_category.toUpperCase()}
            </div>
          </div>
        )}
      </div>

      {/* Badges */}
      {card.badges?.length > 0 && (
        <div style={{ padding: "22px 32px 0" }} data-testid="card-badges">
          <div style={{ fontSize: 8, letterSpacing: "0.4em", color: "#6F6A60", marginBottom: 8 }}>
            COMMENDATIONS · {String(card.badges.length).padStart(2,"0")}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {card.badges.slice(0, 10).map((b) => (
              <span key={b.id} style={{
                fontSize: 8.5, letterSpacing: "0.3em",
                padding: "4px 8px",
                border: `1px solid ${b.kind === "admin" ? pal.accent : "#454038"}88`,
                color: b.kind === "admin" ? pal.accent : "#E4DFD3",
                background: "rgba(0,0,0,0.35)",
              }} data-testid={`card-badge-${b.id}`}>
                {b.label}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      {card.stats && (
        <div style={{ padding: "20px 32px 0" }} data-testid="card-stats">
          <div style={{
            display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8,
            border: `1px solid #2D2A26`, padding: 12, background: "rgba(0,0,0,0.35)",
          }}>
            {[
              { label: "INVESTIGATIONS", value: card.stats.investigations_read },
              { label: "BOOKMARKS", value: card.stats.bookmarks },
              { label: "FOLLOWING", value: card.stats.following },
              { label: "BADGES", value: card.stats.achievements_earned },
            ].map((s) => (
              <div key={s.label} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#E4DFD3" }}>
                  {s.value ?? 0}
                </div>
                <div style={{ fontSize: 7, letterSpacing: "0.35em", color: "#6F6A60", marginTop: 2 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quote */}
      <div style={{ padding: "20px 32px 0", textAlign: "center" }} data-testid="card-quote">
        <div style={{
          fontFamily: "Georgia, serif", fontStyle: "italic",
          fontSize: 14, color: "#A39E93",
          letterSpacing: "0.02em",
        }}>
          &ldquo;{card.quote}&rdquo;
        </div>
      </div>

      {/* Footer: barcode + QR + dates + microtext */}
      <div style={{ position: "absolute", left: 32, right: 32, bottom: 24 }}>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
          <div style={{ flex: 1 }}>
            {/* Barcode */}
            <div style={{ display: "flex", height: 36, alignItems: "stretch", gap: 1 }} data-testid="card-barcode">
              {bars.map((b, i) => (
                <span key={i} style={{
                  width: 2,
                  height: b ? "100%" : "55%",
                  background: b ? "#E4DFD3" : "rgba(228,223,211,0.35)",
                  alignSelf: "flex-end",
                }} />
              ))}
            </div>
            <div style={{
              fontSize: 8, letterSpacing: "0.35em", color: "#6F6A60", marginTop: 6,
            }}>
              ISSUED {issuedShort} · JOINED {joinedShort}
            </div>
            <MicroText>
              ARCHIVE.REPORT · CLASSIFIED · ARCHIVE.REPORT · CLASSIFIED · ARCHIVE.REPORT · CLASSIFIED · ARCHIVE.REPORT
            </MicroText>
            <MicroText>
              {(card.archive_serial + " ").repeat(8)}
            </MicroText>
          </div>
          {/* QR */}
          <div style={{
            padding: 6, background: "#E4DFD3",
            border: `2px solid ${pal.accent}`,
          }} data-testid="card-qr">
            <QRCodeSVG
              value={qrValue}
              size={88}
              level="M"
              bgColor="#E4DFD3"
              fgColor="#08070A"
            />
            <div style={{
              textAlign: "center", marginTop: 4,
              fontSize: 6.5, letterSpacing: "0.3em", color: "#08070A",
            }}>
              SCAN · {pal.label}
            </div>
          </div>
        </div>

        {/* Bottom security band */}
        <div style={{
          marginTop: 12, paddingTop: 8,
          borderTop: `1px dashed ${pal.accent}55`,
          display: "flex", justifyContent: "space-between",
          fontSize: 7.5, letterSpacing: "0.35em", color: "#6F6A60",
        }}>
          <span>// CONTROLLED DOCUMENT · DO NOT FORGE</span>
          <span style={{ color: pal.accent }}>{pal.label}</span>
          <span>FILE · {card.archive_serial}</span>
        </div>
      </div>
    </div>
  );
});

OperativeCard.displayName = "OperativeCard";
export default OperativeCard;
